import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { Box, IconButton, useTheme, Popper } from "@mui/material";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components";
import { useMessagePathById } from "@/shared/domain/topics/hooks/useMessagePath";
import { useTopicById } from "@/shared/domain/topics/hooks/useTopic";
import { usePublishToAiChatPubSubBus } from "@/shared/services/AiChatPubSubBus";
import {
  WORKSPACE_ACTION_TOPIC,
  WorkspaceActionPayload,
} from "@/shared/services/AiChatPubSubBus/models";

import { HighlightedSpan } from "./HighlightedSpan";
import { useAutoNavigation } from "./useAutoNavigation";
import styles from "./VizInternalLink.module.css";

interface VizInternalLinkProps {
  text: string;
  topicId?: string;
  messagePathId?: string;
  time?: bigint;
  className?: string;
  auto?: boolean;
  autoNavProps?: {
    enabled: boolean;
    dedupeKey: string;
  };
}

export const VizInternalLink: React.FC<VizInternalLinkProps> = ({
  text,
  topicId,
  messagePathId,
  time,
  auto,
  autoNavProps,
}) => {
  const theme = useTheme();
  const boxRef = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  const [mouseX, setMouseX] = React.useState(0);

  // Use domain service hooks to fetch data
  const messagePathQuery = useMessagePathById(messagePathId);
  const messagePathTopicQuery = useTopicById(messagePathQuery.data?.topic_id);
  const topicQuery = useTopicById(topicId);

  // Get publish function for workspace actions
  const publishWorkspaceAction =
    usePublishToAiChatPubSubBus<WorkspaceActionPayload>(WORKSPACE_ACTION_TOPIC);

  // Determine which action is available
  const messagePath = messagePathQuery.data;
  const messagePathTopic = messagePathTopicQuery.data;
  const topic = topicQuery.data;

  const openPanelAvailable = !!messagePath && !!messagePathTopic;
  const openSearchAvailable = !!topic;

  const fullyQualifiedMessagePath = React.useMemo(() => {
    if (!messagePath || !messagePathTopic) {
      return undefined;
    }
    return `${messagePathTopic.topic_name}.${messagePath.message_path}`;
  }, [messagePathTopic, messagePath]);

  const topicName = topic?.topic_name;

  const tooltipTitle = React.useMemo(() => {
    if (openPanelAvailable) {
      return `Visualize: ${fullyQualifiedMessagePath}`;
    }
    if (openSearchAvailable) {
      return `See topic: ${topicName}`;
    }
    return "Visualization not available";
  }, [
    openPanelAvailable,
    openSearchAvailable,
    fullyQualifiedMessagePath,
    topicName,
  ]);

  // If we're still loading data, render plain text
  const isLoadingMessagePath = messagePathId && messagePathQuery.isLoading;
  const isLoadingMessagePathTopic =
    messagePathQuery.data?.topic_id && messagePathTopicQuery.isLoading;
  const isLoadingTopic = topicId && topicQuery.isLoading;

  // If no actions are available, render plain text
  const hasAnyAction =
    openPanelAvailable || openSearchAvailable || time !== undefined;

  const handleOpenPanel = React.useCallback(() => {
    if (!messagePath || !messagePathTopic) {
      return false;
    }
    return publishWorkspaceAction({
      type: "open_message_path",
      payload: {
        messagePath,
        topic: messagePathTopic,
        timestamp: time,
      },
    });
  }, [messagePath, messagePathTopic, time, publishWorkspaceAction]);

  const handleOpenSearch = React.useCallback(() => {
    if (!topic) {
      return false;
    }
    return publishWorkspaceAction({
      type: "open_topic_search",
      payload: {
        topic,
        timestamp: time,
      },
    });
  }, [topic, time, publishWorkspaceAction]);

  const handleTimerClick = React.useCallback(() => {
    if (!time) {
      return false;
    }
    const success = publishWorkspaceAction({
      type: "seek_timer",
      payload: { timestamp: time },
    });
    setOpen(false);
    return success;
  }, [time, publishWorkspaceAction]);

  // Use autonavigation hook
  const isLoading =
    isLoadingMessagePath || isLoadingMessagePathTopic || isLoadingTopic;
  const { markAsFollowed } = useAutoNavigation({
    auto,
    autoNavProps,
    entityId: messagePathId ?? topicId,
    time,
    isReady: !isLoading && hasAnyAction,
    onNavigate: () => {
      // Execute the primary action
      if (openPanelAvailable) {
        return handleOpenPanel();
      } else if (openSearchAvailable) {
        return handleOpenSearch();
      } else if (time !== undefined) {
        return handleTimerClick();
      }
      return false;
    },
  });

  // Wrapped handlers that mark as followed on manual clicks
  const handleOpenPanelWithMark = React.useCallback(() => {
    const entityId = messagePathId ?? topicId;
    if (entityId) {
      markAsFollowed(entityId, time);
    }
    const success = handleOpenPanel();
    setOpen(false);
    return success;
  }, [handleOpenPanel, markAsFollowed, messagePathId, topicId, time]);

  const handleOpenSearchWithMark = React.useCallback(() => {
    const entityId = messagePathId ?? topicId;
    if (entityId) {
      markAsFollowed(entityId, time);
    }
    const success = handleOpenSearch();
    setOpen(false);
    return success;
  }, [handleOpenSearch, markAsFollowed, messagePathId, topicId, time]);

  const handleTimerClickWithMark = React.useCallback(() => {
    const entityId = messagePathId ?? topicId;
    if (entityId) {
      markAsFollowed(entityId, time);
    }
    return handleTimerClick();
  }, [handleTimerClick, markAsFollowed, messagePathId, topicId, time]);

  const handleActionClick = React.useCallback((action: () => boolean) => {
    const success = action();
    setOpen(false);
    return success;
  }, []);

  // Early returns after all hooks have been called
  if (isLoadingMessagePath || isLoadingMessagePathTopic || isLoadingTopic) {
    return <span>{text}</span>;
  }

  if (!hasAnyAction) {
    return <span>{text}</span>;
  }

  const handleMouseEnter = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (!boxRef.current) {
      return;
    }

    const rect = boxRef.current.getBoundingClientRect();
    setMouseX(event.clientX - rect.left); // Get relative X position
    setOpen(true);
  };

  return (
    <>
      <HighlightedSpan
        ref={boxRef}
        className={styles.container}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setOpen(false)}
      >
        {text}
      </HighlightedSpan>
      {boxRef.current && (
        <Popper
          open={open}
          anchorEl={boxRef.current}
          placement="top"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [mouseX - boxRef.current.offsetWidth / 2, 4],
              },
            },
          ]}
        >
          <Box
            className={styles.popperBox}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          >
            <Box
              className={styles.popperContent}
              sx={{ backgroundColor: theme.palette.paper.main }}
            >
              <RobotoTooltip title={tooltipTitle} placement="top">
                <span>
                  <IconButton
                    size="small"
                    className={styles.iconButton}
                    onClick={() => {
                      if (openPanelAvailable) {
                        handleActionClick(handleOpenPanelWithMark);
                      } else if (openSearchAvailable) {
                        handleActionClick(handleOpenSearchWithMark);
                      }
                    }}
                    disabled={!openPanelAvailable && !openSearchAvailable}
                  >
                    <QueryStatsIcon className={styles.icon} />
                  </IconButton>
                </span>
              </RobotoTooltip>
              <RobotoTooltip
                title={
                  time !== undefined
                    ? "Jump to timestamp"
                    : "No timestamp available"
                }
                placement="top"
              >
                <span>
                  <IconButton
                    size="small"
                    className={styles.iconButton}
                    onClick={handleTimerClickWithMark}
                    disabled={time === undefined}
                  >
                    <TimerOutlinedIcon className={styles.icon} />
                  </IconButton>
                </span>
              </RobotoTooltip>
            </Box>
          </Box>
        </Popper>
      )}
    </>
  );
};
